import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callPostApi } from "../../function/ApiCallFunction";
import { Button, Box, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { CASHI_TAGLINE, cashiLogo } from "../../include/cashiBrand";

function Login({ onLogin }) {
  const navigate = useNavigate();
  const { setLoginData } = useContext(DataContext);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (event) => {
    event.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    setLoading(true);
    try {
      const result = await callPostApi("store-login", { mod: "STORE_SEND_OTP", data_arr: { email: email.trim() } });
      if (result.status === "200") {
        setSentTo(result.data?.email || email.trim());
        setOtp("");
        toast.success(result.data?.message || "A 6-digit code was sent to this email.");
      } else toast.error(result?.status_message || result?.data?.error || "Could not send the code", { autoClose: 2500 });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const result = await callPostApi("store-login", { mod: "STORE_VERIFY_OTP", data_arr: { email: sentTo || email.trim(), otp: otp.trim() } });
      if (result.status === "200" && result.data) {
        toast.success("Login successfully!", { autoClose: 1000 });
        setLoginData(result.data);
        sessionStorage.setItem("loginData", JSON.stringify(result.data));
        onLogin(result.data);
      } else toast.error(result?.status_message || result?.data?.error || "That code is not correct", { autoClose: 2500 });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", background: "#fff" }}>
      <Box sx={{ display: { xs: "none", md: "flex" }, width: "46%", background: consoleColors.sidebar, color: "#fff", flexDirection: "column", justifyContent: "space-between", p: 6 }}>
        <Box>
          <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 46, width: "auto" }} />
          <Typography sx={{ fontSize: 22, fontWeight: 650, lineHeight: 1.4, mt: 4, maxWidth: 420 }}>
            {CASHI_TAGLINE}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.45, mt: 2, maxWidth: 420, color: consoleColors.navText }}>
            Coupons, loyalty, and QR codes for your store.
          </Typography>
        </Box>
        <Typography sx={{ color: consoleColors.navText, fontSize: 14 }}>Store sign in</Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3, background: consoleColors.page }}>
        <Box component="form" onSubmit={sentTo ? verifyCode : sendCode} sx={{ width: "100%", maxWidth: 400, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: 4 }}>
          <Box sx={{ display: { xs: "block", md: "none" }, mb: 2.5 }}>
            <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 32, width: "auto" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 650, color: consoleColors.ink, mt: 1.2, lineHeight: 1.4 }}>{CASHI_TAGLINE}</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: consoleColors.muted, fontWeight: 700, letterSpacing: 0.6 }}>STORE</Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, color: consoleColors.ink, mt: 0.5, mb: 1 }}>{sentTo ? "Enter code" : "Sign in"}</Typography>
          {sentTo ? (
            <>
              <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 2, lineHeight: 1.45 }}>
                Enter the 6-digit code for {sentTo}.
              </Typography>
              <TextField
                fullWidth
                label="6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6 }}
              />
              <Button type="submit" fullWidth disabled={loading} variant="contained" sx={{ ...primaryButtonSx, mt: 3, py: 1.2 }}>
                {loading ? "Signing in..." : "Sign in"}
              </Button>
              <Button type="button" fullWidth disabled={loading} onClick={sendCode} sx={{ mt: 1.2, textTransform: "none", color: consoleColors.accent, fontWeight: 650 }}>
                Send a new code
              </Button>
              <Button type="button" fullWidth onClick={() => { setSentTo(""); setOtp(""); }} sx={{ mt: 0.4, textTransform: "none", color: consoleColors.muted }}>
                Use a different email
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 2, lineHeight: 1.45 }}>
                Enter the store email. We will send a 6-digit login code.
              </Typography>
              <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Button type="submit" fullWidth disabled={loading} variant="contained" sx={{ ...primaryButtonSx, mt: 3, py: 1.2 }}>
                {loading ? "Sending..." : "Send code"}
              </Button>
              <Button
                type="button"
                fullWidth
                disabled={loading}
                onClick={() => {
                  if (!email.trim()) { toast.error("Email is required"); return; }
                  setSentTo(email.trim());
                  setOtp("");
                }}
                sx={{ mt: 1.2, textTransform: "none", color: consoleColors.accent, fontWeight: 650 }}
              >
                I already have a code
              </Button>
              <Button type="button" fullWidth onClick={() => navigate("/storeregister")} sx={{ mt: 1.2, py: 1.15, textTransform: "none", fontWeight: 650, color: consoleColors.ink, border: `1px solid ${consoleColors.line}`, borderRadius: 1 }}>
                Sign up
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
